import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { TrendingUp, Mail, Lock, User, Eye, Users, Gift } from "lucide-react";
import { z } from "zod";
import { lovable } from "@/integrations/lovable/index";

import authBg from "@/assets/auth-bg.jpg";

const emailSchema = z.string().trim().email("Invalid email address").max(255);
const passwordSchema = z.string().min(6, "Password must be at least 6 characters").max(72);
const displayNameSchema = z.string().trim().max(100).optional();

interface Props {
  isRTL: boolean;
}

export const AuthForm = ({ isRTL }: Props) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedRole, setSelectedRole] = useState<"trader" | "ib">("trader");
  const [referralCode, setReferralCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const { signIn, signUp } = useAuth();
  const { toast } = useToast();

  const normalizeReferralCode = (rawCode: string) => {
    const normalized = rawCode.trim().toUpperCase().replace(/\s+/g, "");
    if (!normalized) return "";
    if (normalized.startsWith("JRX-")) return normalized;
    if (/^[A-Z0-9]{8,}$/.test(normalized)) return `JRX-${normalized}`;
    return normalized;
  };

  // Prefill referral code from URL ?ref=CODE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = normalizeReferralCode(params.get("ref") || "");
    const refFromSession = normalizeReferralCode(sessionStorage.getItem("jourex_referral_code") || "");
    const ref = refFromUrl || refFromSession;
    if (ref) {
      setReferralCode(ref);
      setIsLogin(false);
    }

    if (refFromUrl) {
      sessionStorage.setItem("jourex_referral_code", refFromUrl);
    }
  }, []);


  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const normalizedReferralCode = normalizeReferralCode(referralCode);
      if (normalizedReferralCode) {
        sessionStorage.setItem("jourex_referral_code", normalizedReferralCode);
      }
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        toast({
          title: isRTL ? "خطا" : "Error",
          description: result.error.message,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);
      if (!isLogin) displayNameSchema.parse(displayName);
      const normalizedReferralCode = normalizeReferralCode(referralCode);

      const result = isLogin
        ? await signIn(email, password)
        : await signUp(email, password, displayName || undefined, selectedRole, normalizedReferralCode || undefined);

      if (result.error) {
        let msg = result.error.message;
        if (msg.includes("User already registered")) msg = isRTL ? "این ایمیل قبلاً ثبت شده است" : "This email is already registered";
        else if (msg.includes("Invalid login credentials")) msg = isRTL ? "ایمیل یا رمز عبور اشتباه است" : "Invalid email or password";
        toast({ title: isRTL ? "خطا" : "Error", description: msg, variant: "destructive" });
      } else if (!isLogin) {
        sessionStorage.removeItem("jourex_referral_code");
        const desc = selectedRole === "ib"
          ? (isRTL ? "حساب شما ایجاد شد. حساب IB در انتظار تأیید ادمین است." : "Account created. Your IB account is pending admin approval.")
          : (isRTL ? "حساب کاربری ایجاد شد" : "Account created successfully");
        toast({ title: isRTL ? "موفق" : "Success", description: desc });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: isRTL ? "خطا" : "Validation Error", description: error.errors[0].message, variant: "destructive" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    { value: "trader" as const, label: isRTL ? "معامله‌گر" : "Trader", icon: TrendingUp, disabled: false },
    { value: "ib" as const, label: isRTL ? "همکار (IB)" : "IB (Affiliate)", icon: Users, disabled: false },
    { value: "investor" as const, label: isRTL ? "سرمایه‌گذار" : "Investor", icon: Eye, disabled: true },
  ];

  return (
    <div className="flex-1 flex items-center justify-center relative p-6 lg:p-12">
      <img src={authBg} alt="" className="absolute inset-0 w-full h-full object-cover lg:hidden" aria-hidden="true" />
      <div className="absolute inset-0 bg-background/90 lg:bg-background" />

      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl p-8 shadow-2xl">
          {/* Mobile brand */}
          <div className="flex justify-center lg:hidden mb-4">
            <div className="p-3 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-foreground">
              {isRTL ? "ژورنال معاملات فارکس" : "Forex Trade Journal"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {isLogin
                ? (isRTL ? "وارد حساب کاربری شوید" : "Sign in to your account")
                : (isRTL ? "حساب کاربری جدید بسازید" : "Create a new account")}
            </p>
          </div>

          {/* Referral code field shown above Google button when in signup mode */}
          {!isLogin && (
            <div className="space-y-2 mb-4">
              <Label htmlFor="referralCode" className="text-muted-foreground text-xs">
                {isRTL ? "کد معرف (اختیاری)" : "Referral / Invite Code (optional)"}
              </Label>
              <div className="relative">
                <Gift className={`absolute top-3 h-4 w-4 text-muted-foreground ${isRTL ? "right-3" : "left-3"}`} />
                <Input
                  id="referralCode"
                  type="text"
                  placeholder={isRTL ? "مثلاً: REF123" : "e.g. REF123"}
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  onBlur={() => {
                    const normalized = normalizeReferralCode(referralCode);
                    if (normalized) setReferralCode(normalized);
                  }}
                  className={isRTL ? "pr-10" : "pl-10"}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {isRTL ? "در صورت معتبر بودن، مزایای معرفی پس از ثبت نام اعمال می‌شود." : "If valid, referral benefits will be applied after signup."}
              </p>
            </div>
          )}

          {/* Google Sign-In */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 mb-4 gap-2 font-medium"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isGoogleLoading
              ? (isRTL ? "لطفاً صبر کنید..." : "Please wait...")
              : (isRTL ? "ادامه با گوگل" : "Continue with Google")}
          </Button>

          {/* Divider */}
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card/80 px-2 text-muted-foreground">
                {isRTL ? "یا" : "or"}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Role Selector (signup only) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label>{isRTL ? "نوع حساب" : "Account Type"}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {roleOptions.map(({ value, label, icon: Icon, disabled }) => (
                    <button
                      key={value}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setSelectedRole(value as "trader" | "ib")}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm relative ${
                        disabled
                          ? "border-border/30 bg-muted/30 text-muted-foreground/50 cursor-not-allowed"
                          : selectedRole === value
                            ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                            : "border-border hover:border-muted-foreground/30 text-muted-foreground"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{label}</span>
                      {disabled && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-500">
                          {isRTL ? "به‌زودی" : "Coming Soon"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="displayName">{isRTL ? "نام نمایشی" : "Display Name"}</Label>
                <div className="relative">
                  <User className={`absolute top-3 h-4 w-4 text-muted-foreground ${isRTL ? "right-3" : "left-3"}`} />
                  <Input id="displayName" type="text" placeholder={isRTL ? "نام شما" : "Your name"} value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={isRTL ? "pr-10" : "pl-10"} />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{isRTL ? "ایمیل" : "Email"}</Label>
              <div className="relative">
                <Mail className={`absolute top-3 h-4 w-4 text-muted-foreground ${isRTL ? "right-3" : "left-3"}`} />
                <Input id="email" type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={isRTL ? "pr-10" : "pl-10"} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{isRTL ? "رمز عبور" : "Password"}</Label>
              <div className="relative">
                <Lock className={`absolute top-3 h-4 w-4 text-muted-foreground ${isRTL ? "right-3" : "left-3"}`} />
                <Input id="password" type="password" placeholder={isRTL ? "حداقل ۶ کاراکتر" : "At least 6 characters"} value={password} onChange={(e) => setPassword(e.target.value)} className={isRTL ? "pr-10" : "pl-10"} required />
              </div>
            </div>

            <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold h-11" disabled={isLoading}>
              {isLoading
                ? (isRTL ? "لطفاً صبر کنید..." : "Please wait...")
                : isLogin
                  ? (isRTL ? "ورود" : "Sign In")
                  : (isRTL ? "ثبت نام" : "Sign Up")}
            </Button>
          </form>

          <div className="text-center mt-5">
            <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-sm text-emerald-700 hover:text-emerald-600 hover:underline transition">
              {isLogin
                ? (isRTL ? "حساب کاربری ندارید؟ ثبت نام کنید" : "Don't have an account? Sign up")
                : (isRTL ? "قبلاً ثبت نام کرده‌اید؟ وارد شوید" : "Already have an account? Sign in")}
            </button>
          </div>
          <div className="flex justify-center gap-3 mt-4 text-xs text-muted-foreground">
            <a href="/terms" className="hover:underline">Terms</a>
            <span>·</span>
            <a href="/privacy" className="hover:underline">Privacy</a>
            <span>·</span>
            <a href="/refund" className="hover:underline">Refund Policy</a>
          </div>
        </div>
      </div>
    </div>
  );
};
